<template>
  <div class="pta-card-session">
    <div class="pta-card-session__body">
      <div class="pta-card-session__title-wrapper">
        <div class="pta-card-session__title-group">
          <h2 class="pta-card-session__title">
            {{ redeableStatus[sessionStatus] }}
            <LabStatusIcon
              class="pta-card-session__status-icon"
              :status="sessionStatus"
            />
          </h2>
          <div class="pta-card-session__subtitle">
            <p
              v-if="!!waitingTime && waitingIntervalId"
              class="pta-card-session__time-running"
            >
              STARTING IN: {{ formatRunningTime(waitingTime) }}
            </p>
            <p
              v-if="canShowSessionDuration"
              class="pta-card-session__time-running"
            >
              TIME RUNNING: {{ formatRunningTime(runningTime) }}
            </p>
            <template v-if="isEDA || isICCA || isWPT || isCDFP">
              <p
                v-if="isEDA && sessionStatus === sessionStatuses.IDDLE"
                class="pta-card-session__help-text"
                v-html="iddleHelpTextEDA"
              ></p>
              <p
                v-if="isICCA && sessionStatus === sessionStatuses.IDDLE"
                class="pta-card-session__help-text"
                v-html="iddleHelpTextICCA"
              ></p>
              <p
                v-if="isWPT && sessionStatus === sessionStatuses.IDDLE"
                class="pta-card-session__help-text"
                v-html="iddleHelpTextWPT"
              ></p>
              <p
                v-if="isCDFP && sessionStatus === sessionStatuses.IDDLE"
                class="pta-card-session__help-text"
                v-html="iddleHelpTextCDFP"
              ></p>
            </template>
            <p
              v-else-if="!!helpTexts[sessionStatus]"
              class="pta-card-session__help-text"
            >
              {{ helpTexts[sessionStatus] }}
            </p>
          </div>
        </div>
        <PTACardGlobalSession
          v-if="isOtherSessionRunning"
          class="pta-card-session__session-details"
          :currentLab="currentLab"
          :currentSession="currentSession"
          :labsRequestPayload="labsRequestPayload"
        />
      </div>
      <div class="pta-card-session__actions-wrapper">
        <div
          v-if="
            labHasStarted &&
            !labWithFlags &&
            !labWithObjectives &&
            isNotCertification
          "
          class="pta-card-session__actions"
        >
          <div class="pta-card-session__actions__switcher">
            <span
              class="pta-card-session__actions__switcher__text"
              :class="{
                'pta-card-session__actions__switcher__text--disabled':
                  isChangingState || isDelayed,
              }"
            >
              {{ switcherText }}
            </span>
            <SwitchCheckbox
              v-model="isLabFinished"
              :disabled="isChangingState || isDelayed"
            />
          </div>
        </div>
        <div class="pta-card-session__buttons">
          <div
            v-if="shouldShowRegionSelector"
            class="pta-card-session__region-info-wrapper"
          >
            <InfoIcon
              v-tooltip="{
                content: `Select the region closest <br/>to you to reduce latency <br/>and improve lab performance`,
                placement: 'top-center',
                classes: ['home-bootcamp__tooltip'],
              }"
              class="pta-card-session__region-info-icon"
            />
            Select the region closest to you
          </div>
          <div
            v-if="shouldShowRegionSelector"
            class="pta-card-session__region-selector__wrapper"
          >
            <Dropdown
              class="pta-card-session__region-selector"
              :options="regions"
              :handleSelection="dropdownHandler"
              :selectedOption="{ label: regionSelected || 'Select Region' }"
            />
            <div
              v-if="showRegionRequired"
              class="pta-card-session__region-info-error"
            >
              * Required
            </div>
          </div>
          <LabKeyboardSelection
            v-if="shouldShowRegionSelector"
            @keyboad-changed="changeKeyboardSelection"
          />
          <div
            v-tooltip="{
              content: isCtaDisabled
                ? `Complete required fields<br/>to start the lab`
                : null,
              placement: 'top-center',
              classes: ['home-bootcamp__tooltip'],
            }"
            class="pta-card-session__action-buttons"
            :class="isCtaDisabled && 'pta-card-session__btn--disabled'"
          >
            <button
              class="pta-card-session__btn"
              :class="[
                lasSessionRunning
                  ? 'btn btn--tertiary'
                  : startWithPrimary
                  ? 'btn btn--primary'
                  : 'btn btn--secondary',
              ]"
              :disabled="isCtaDisabled"
              @click="handleSession"
            >
              {{ ctaTexts[sessionStatus] }}
            </button>
            <a
              v-if="lasSessionRunning"
              class="btn btn--secondary pta-card-session__buttons__open-lab"
              :href="currentSession.pta_url"
              target="_blank"
            >
              Open lab
            </a>
          </div>
        </div>
      </div>
    </div>
    <PTACatchTheFlag
      v-if="labWithFlags"
      :currentLab="currentLab"
      :sessionStatus="sessionStatus"
      :labsRequestPayload="labsRequestPayload"
    />
    <DialogModal
      v-if="showIdleModal && !isExam"
      @close-dialog-modal="closeModal"
    >
      <p class="dialog-modal__title">Are you still working on this lab?</p>
      <div class="dialog-modal__actions-wrapper">
        <button class="btn btn--primary" @click="handlePingInterval">
          Yes
        </button>
        <button class="btn btn--secondary" @click="closeModal">No</button>
      </div>
    </DialogModal>
    <LabObjectives
      v-if="
        currentLab.lab_objectives &&
        currentLab.lab_objectives.length &&
        isNotCertification
      "
      :labObjectivesPlain="currentLab.lab_objectives"
      :userLabStatus="currentLab.user_lab_status"
      :userLabAttempt="currentLab.user_lab_attempt"
      :labStatus="sessionStatus"
    />
  </div>
</template>

<script>
import { format } from 'date-fns'
import { mapState } from 'vuex'
import { InfoIcon } from 'vue-feather-icons'
import DialogModal from '@/components/modals/DialogModal.vue'
import CONSTANTS from '@/constants'
import SwitchCheckbox from '@/components/forms/elements/SwitchCheckbox.vue'
import PTACardGlobalSession from '@/components/modals/lab-modal/PTACardGlobalSession.vue'
import LabStatusIcon from '@/components/modals/lab-modal/LabStatusIcon.vue'
import PTACatchTheFlag from '@/components/modals/lab-modal/PTACatchTheFlag.vue'
import LabKeyboardSelection from '@/components/modals/lab-modal/LabKeyboardSelection.vue'
import LabObjectives from '@/components/modals/lab-modal/LabObjectives.vue'
import Dropdown from '@/components/forms/elements/Dropdown.vue'
import { getDarklyFlagKeyValue } from '@/utils'

export default {
  name: 'PTACardCurrentSession',
  components: {
    DialogModal,
    SwitchCheckbox,
    PTACardGlobalSession,
    LabStatusIcon,
    PTACatchTheFlag,
    InfoIcon,
    LabKeyboardSelection,
    LabObjectives,
    Dropdown,
  },
  props: {
    currentLab: {
      type: Object,
      required: true,
    },
    currentSession: {
      type: Object,
      required: false,
      default: () => {
        return null
      },
    },
    isExam: {
      type: Boolean,
      required: false,
    },
    isEDA: {
      type: Boolean,
      required: false,
    },
    isICCA: {
      type: Boolean,
      required: false,
    },
    isWPT: {
      type: Boolean,
      required: false,
    },
    isCDFP: {
      type: Boolean,
      required: false,
    },
    setLabStatus: {
      type: Function,
      required: true,
    },
    context: {
      type: String,
      required: false,
    },
    parentCourseId: {
      type: String,
      required: false,
    },
    mixpanelEventPayload: {
      type: Object,
      required: true,
    },
    startWithPrimary: {
      type: Boolean,
      required: false,
      default: () => {
        return false
      },
    },
    startDisabled: {
      type: Boolean,
      required: false,
      default: () => {
        return false
      },
    },
    labsRequestPayload: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      isLoadingLab: false,
      isLoadingSession: false,
      sessionRefreshIntervalId: null,
      sessionPingIntervalId: null,
      runningTime: null,
      runningTimeIntervalId: null,
      refreshSeconds: 5,
      pingSeconds: 60,
      showIdleModal: false,
      timeIdleModal: 0,
      markingActive: null,
      iddleHelpTextEDA: `When you are ready to begin the “Lab Questions” portion of your exam, please open the “<a href="https://drive.google.com/file/d/14jk74O3-ZU_gY1lMgPqjB6SxbjCn-nzw/view" target="_blank">eEDA Statement of Work</a>”. This document contains the usernames and passwords you will need for the exam’s lab. Next, please choose the region closest to you for the best experience. Once you have chosen the region closest to you and clicked “Start Lab”, the lab will generate in a new tab. Please keep both of these tabs open as you complete the lab-dependent portion of the exam. Once the lab has been generated, you may begin answering the lab-dependent questions on this exam.`,
      iddleHelpTextICCA: `When you are ready to begin the “Lab Questions” portion of your exam, please choose the region closest to you for the best experience. Once you have chosen the region closest to you and clicked “Start Lab”, the lab will generate in a new tab. Please keep both of these tabs open as you complete the hands-on lab portion of the exam. Once the lab has been generated, you may begin completing the lab objectives listed below.`,
      iddleHelpTextWPT: `Please start your exam by activating the lab. Once you have chosen the region closest to you and clicked “Start Lab,” the lab will generate in a secondary tab. <u>Please keep both of these tabs open for the duration of the exam</u>. Once the lab has been generated, you may begin answering the multiple-choice questions on this exam. Please note that the multiple-choice questions are dependent on the lab environment.`,
      iddleHelpTextCDFP: `When you are ready to begin the “Lab-Questions” Multiple-Choice portion of the exam, please choose the region closest to you for the best experience. Once you have chosen the region closest to you and clicked “Start Lab,” the lab will generate in a secondary tab. <u>Please keep both of these tabs open for the duration of the exam.</u> Once the lab has been generated, you may begin answering the lab-dependent multiple-choice questions on this exam. You will not need to access the lab for the first part of the exam.`,
      helpTexts: {
        iddle:
          'For the best experience, choose the region closest to you. Then, start the lab to begin.',
        starting:
          "Please keep this window open while you're working on the lab. Labs that don't register activity will be automatically stopped.",
        started:
          "Please keep this window open while you're working on the lab. Labs that don't register activity will be automatically stopped.",
        stopping: '',
        stopped: '',
        terminating: '',
        terminated: '',
      },
      redeableStatus: {
        iddle: 'Not running',
        starting: 'Lab Starting',
        started: 'Lab Running',
        stopping: 'Lab Stopping',
        stopped: 'Lab Stopped',
        terminating: 'Lab Stopped',
        terminated: 'Lab Stopped',
      },
      ctaTexts: {
        iddle: 'Start lab',
        starting: 'Start lab',
        started: 'Stop lab',
        stopping: 'Stop lab',
        stopped: 'Resume lab',
        terminating: 'Start lab',
        terminated: 'Start lab',
      },
      isChangingState: false,
      isDelayed: false,
      startingLab: false, // we force this state to show loading
      stoppingLab: false, // we force this state to show loading
      waitingIntervalId: null,
      waitingTime: CONSTANTS.LABS.FIXED_PARAMS.WAITING_TIME,
      regionSelected: '',
      currentKeyboardSelected: '',
      showRegionRequired: false,
    }
  },
  computed: {
    ...mapState({
      course: state => state.courses.currentCourse,
      activeCourses: state => state.courses.activeCourses,
    }),
    switcherText() {
      if (this.isLabFinished) {
        return 'Mark unfinished'
      }
      return 'Mark finished'
    },
    regions() {
      return CONSTANTS.LABS.REGIONS
    },
    sessionStatuses() {
      return CONSTANTS.LABS.SESSION_STATUS
    },
    shouldShowRegionSelector() {
      return (
        !this.globalRunningSession &&
        this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.IDDLE
      )
    },
    lasSessionRunning() {
      if (!this.sessionStatus) return false
      const sessionTransitioningState = [CONSTANTS.LABS.SESSION_STATUS.STARTED]
      return sessionTransitioningState.includes(this.sessionStatus)
    },
    isLabFinished: {
      get() {
        return (
          this.currentLab.user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
        )
      },
      set() {
        this.toggleLabStatus()
      },
    },
    sessionStatus() {
      if (this.startingLab || (this.waitingIntervalId && !!this.waitingTime))
        return CONSTANTS.LABS.SESSION_STATUS.STARTING
      if (this.stoppingLab) return CONSTANTS.LABS.SESSION_STATUS.STOPPING
      return this.currentSession &&
        this.currentSession.status &&
        !this.sessionsToIgnore.includes(this.currentSession.status)
        ? this.currentSession.status
        : CONSTANTS.LABS.SESSION_STATUS.IDDLE
    },
    sessionsToIgnore() {
      return [
        CONSTANTS.LABS.SESSION_STATUS.TERMINATED,
        CONSTANTS.LABS.SESSION_STATUS.ERROR,
      ]
    },
    isInitialStatus() {
      return (
        !this.sessionStatus ||
        ['terminating', 'terminated', 'error'].includes(this.sessionStatus)
      )
    },
    labFinishedTime() {
      return format(
        new Date(this.currentLab.user_status_modified),
        'LLLL d, yyyy hh:mm a'
      )
    },
    globalRunningSession() {
      return this.currentLab.global_running_session
    },
    globalRunningLabName() {
      return this.globalRunningSession && this.globalRunningSession.lab.name
    },
    isOtherSessionRunning() {
      const currentSessionId = this.currentSession
        ? this.currentSession.id
        : null
      return (
        this.globalRunningSession &&
        this.globalRunningSession.id !== currentSessionId
      )
    },
    shouldCreateRefreshInterval() {
      const sessionRefreshStatuses = [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
      ]
      return sessionRefreshStatuses.includes(this.currentSession?.status)
    },
    shouldCreatePingInterval() {
      const sessionPingStatuses = [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
      ]
      return sessionPingStatuses.includes(this.sessionStatus)
    },
    activeCourse() {
      if (this.context === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COURSE) {
        return !!this.activeCourses.find(
          activeCourse => activeCourse.id === this.parentCourseId
        )
      }
      return false
    },
    isTransitioningStates() {
      if (!this.currentSession) return false
      const sessionTransitioningState = [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
      ]
      return sessionTransitioningState.includes(this.sessionStatus)
    },
    labHasStarted() {
      if (!this.currentLab) return false
      const userHasStartedStates = [
        CONSTANTS.CONTENT_USER_STATUS.FINISHED,
        CONSTANTS.CONTENT_USER_STATUS.STARTED,
      ]
      return userHasStartedStates.includes(this.currentLab.user_status)
    },
    canShowSessionDetails() {
      if (!this.currentSession) return false
      const sessionTransitioningState = [
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
        CONSTANTS.LABS.SESSION_STATUS.STOPPED,
      ]
      return sessionTransitioningState.includes(this.sessionStatus)
    },
    canShowSessionDuration() {
      if (!this.currentSession || this.stoppingLab) return false
      const sessionTransitioningState = [
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
        CONSTANTS.LABS.SESSION_STATUS.STOPPED,
      ]
      return sessionTransitioningState.includes(this.sessionStatus)
    },
    labWithFlags() {
      return this.currentLab.flags && this.currentLab.flags.length
    },
    labWithObjectives() {
      return (
        this.currentLab.lab_objectives && this.currentLab.lab_objectives.length
      )
    },
    noRegionSelected() {
      return !this.regionSelected
    },
    isNotCertification() {
      return this.context !== CONSTANTS.PARENT_CONTENT_TYPES_KEYS.CERTIFICATION
    },
    isCtaDisabled() {
      return (
        this.isLoadingSession ||
        this.isOtherSessionRunning ||
        this.isTransitioningStates ||
        this.noRegionSelected
      )
    },
  },
  watch: {
    currentSession: {
      immediate: true,
      handler(newValue, oldValue) {
        const { STARTING, STARTED } = CONSTANTS.LABS.SESSION_STATUS
        this.handleRefreshInterval()
        this.handlePingInterval()
        this.handleRunningTimeInterval()
        if (oldValue?.status === STARTING && newValue.status === STARTED) {
          this.handleWaitingTime()
        }
      },
    },
    sessionStatus: {
      immediate: true,
      handler() {
        this.$emit('status-has-changed', this.sessionStatus)
      },
    },
  },
  beforeDestroy() {
    this.sessionRefreshIntervalId = clearInterval(this.sessionRefreshIntervalId)
    this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
    this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
    this.markingActive = clearTimeout(this.markingActive)
  },
  created() {
    this.regionSelected = localStorage.getItem(`pta-lab-region`) || ''
  },
  methods: {
    formatRunningTime(secs) {
      const sec_num = parseInt(secs, 10)
      const hours = Math.floor(sec_num / 3600)
      const minutes = Math.floor(sec_num / 60) % 60
      const seconds = sec_num % 60
      return [hours, minutes, seconds]
        .map(v => (v < 10 ? `0${v}` : v))
        .join(':')
    },
    async handleSession() {
      if (this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.IDDLE) {
        await this.createSession()
      } else if (this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.STARTED) {
        this.stoppingLab = true
        await this.updateSessionStatus(CONSTANTS.LABS.SESSION_STATUS.TERMINATED)
        setTimeout(() => {
          // Need to update Skill Dive remaining time, we give the API 1 sec to sync
          this.$store.dispatch('auth/userMeOnContent')
        }, 1000)
        this.stoppingLab = false
      }
    },
    async createSession() {
      try {
        this.isLoadingSession = true
        this.startingLab = true

        const response = await this.$store.dispatch('labs/createLabSession', {
          region: this.regionSelected,
          keyboard_layout: this.currentKeyboardSelected,
          ...this.labsRequestPayload,
        })
        localStorage.setItem(`pta-lab-region`, this.regionSelected)

        if (
          this.currentLab.user_status ===
          CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
        )
          this.setLabStatus('started')

        this.startingLab = false
        if (response && response.errorCode) {
          this.$emit('custom-lab-error', response.errorCode)
          this.sendMixpanelEvent(
            this.eventsConstants.CONTENT_LAB_SESSION_ERROR,
            { ...this.mixpanelEventPayload, error_code: response.errorCode }
          )
        } else {
          this.isLoadingSession = false
          if (
            this.context === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COURSE &&
            !this.activeCourse
          ) {
            setTimeout(async () => {
              this.markingActive = await this.markCourseAsActive()
            }, 15000)
          }

          this.sendMixpanelEvent(
            this.eventsConstants.CONTENT_LAB_SESSION_STARTED,
            this.mixpanelEventPayload
          )
        }
      } catch (error) {
        this.startingLab = false
        this.isLoadingSession = false
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_LAB_SESSION_ERROR, {
          ...this.mixpanelEventPayload,
          error_code: error,
        })
      }
    },
    async getSession() {
      try {
        await this.$store.dispatch('labs/getLabSession', {
          labId: this.currentLab.id,
          sessionId: this.currentSession.id,
          ...this.labsRequestPayload,
        })
      } catch (error) {
        this.sessionRefreshIntervalId = clearInterval(
          this.sessionRefreshIntervalId
        )
      }
    },
    async createPing() {
      const response = await this.$store.dispatch(
        'labs/createLabPing',
        this.labsRequestPayload
      )
      if (response) {
        // if error we should get Lab to update session if there is any, this will stop future pings with shouldCreatePingInterval
        await this.$store.dispatch('labs/getLabById', this.labsRequestPayload)
      }
    },
    async updateSessionStatus(status) {
      this.isLoadingSession = true
      const response = await this.$store.dispatch(
        'labs/partialUpdateLabSession',
        {
          labId: this.currentLab.id,
          sessionId: this.currentSession.id,
          status,
          ...this.labsRequestPayload,
        }
      )
      await this.$store.dispatch('labs/getLabById', this.labsRequestPayload)

      if (response && response.errorCode) {
        this.$emit('custom-lab-error', response.errorCode)
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_LAB_SESSION_ERROR, {
          ...this.mixpanelEventPayload,
          error_code: response.errorCode,
        })
      } else {
        this.isLoadingSession = false

        if (status === CONSTANTS.LABS.SESSION_STATUS.STARTED) {
          this.sendMixpanelEvent(
            this.eventsConstants.CONTENT_LAB_SESSION_STARTED,
            this.mixpanelEventPayload
          )
        } else if (status === CONSTANTS.LABS.SESSION_STATUS.STOPPED) {
          this.sendMixpanelEvent(
            this.eventsConstants.CONTENT_LAB_SESSION_STOPPED,
            this.mixpanelEventPayload
          )
        }
      }
    },
    async toggleLabStatus() {
      const isSetStatusDelayed = await getDarklyFlagKeyValue(
        'setContentStatusDelayActive'
      )
      if (isSetStatusDelayed) {
        this.isDelayed = true
        setTimeout(() => {
          this.isDelayed = false
        }, process.env.VUE_APP_SET_CONTENT_STATUS_DELAY)
      }

      this.isChangingState = true
      await this.setLabStatus(
        this.isLabFinished
          ? CONSTANTS.CONTENT_USER_STATUS.STARTED
          : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      )
      this.isChangingState = false
    },
    increaseRunningTime() {
      this.runningTime += 1
      this.timeIdleModal += 1
      this.checkIdleTime()
    },
    decreaseWaitingTime() {
      this.waitingTime -= 1
      this.checkWaitingTime()
    },
    setInitialRunningTime() {
      this.runningTime = this.currentSession
        ? this.currentSession.running_time
        : 0
    },
    setInitialWaitingTime() {
      this.waitingTime = CONSTANTS.LABS.FIXED_PARAMS.WAITING_TIME
    },
    handleRefreshInterval() {
      if (this.shouldCreateRefreshInterval && !this.sessionRefreshIntervalId) {
        this.sessionRefreshIntervalId = setInterval(
          this.getSession,
          this.refreshSeconds * 1000
        )
      } else if (!this.shouldCreateRefreshInterval) {
        this.sessionRefreshIntervalId = clearInterval(
          this.sessionRefreshIntervalId
        )
      }
    },
    handleWaitingTime() {
      this.setInitialWaitingTime()
      if (this.waitingTime > 1) {
        this.waitingIntervalId = setInterval(this.decreaseWaitingTime, 1000)
      } else {
        this.waitingIntervalId = clearInterval(this.waitingIntervalId)
        this.handleRunningTimeInterval()
      }
    },
    handlePingInterval() {
      if (this.shouldCreatePingInterval && !this.sessionPingIntervalId) {
        this.sessionPingIntervalId = setInterval(
          this.createPing,
          this.pingSeconds * 1000
        )
        this.timeIdleModal = 0
        this.showIdleModal = false
      } else if (!this.shouldCreatePingInterval) {
        this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
      }
    },
    handleRunningTimeInterval() {
      this.setInitialRunningTime()
      if (this.sessionStatus === 'started' && !this.runningTimeIntervalId) {
        this.runningTimeIntervalId = setInterval(this.increaseRunningTime, 1000)
      } else if (this.sessionStatus !== 'started') {
        this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
      }
    },
    closeModal() {
      this.showIdleModal = false
      this.$emit('close-modal')
    },
    checkIdleTime() {
      if (this.timeIdleModal >= this.currentLab.duration_in_seconds * 2) {
        this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
        this.showIdleModal = true
      }
    },
    checkWaitingTime() {
      if (this.waitingTime < 1) {
        this.waitingIntervalId = clearInterval(this.waitingIntervalId)
        this.handleRunningTimeInterval()
      }
    },
    async markCourseAsActive() {
      await this.$store.dispatch('courses/addActiveCourse', this.parentCourseId)
      await this.$store.dispatch('courses/getActiveCourses')
      if (this.activeCourse) {
        this.sendMixpanelEvent(
          this.eventsConstants.CONTENT_COURSE_ACTIVE_ADDED,
          {
            course_id: this.activeCourse.id,
            course_name: this.activeCourse.name,
            trigger: 'lab',
            ...this.activeCourse,
          }
        )
      }
    },
    changeKeyboardSelection(keyboardSelected) {
      this.currentKeyboardSelected = keyboardSelected
    },
    dropdownHandler(regionSelected) {
      this.showRegionRequired = false
      if (this.regionSelected && regionSelected === '') {
        this.showRegionRequired = true
      }
      this.regionSelected = regionSelected
    },
  },
}
</script>

<style lang="scss" scoped>
.pta-card-session {
  max-width: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  align-items: flex-start;
  justify-content: center;
  &__body {
    text-align: left;
    display: flex;
    align-items: flex-start;
    width: 100%;
    padding-bottom: 40px;
    align-content: flex-start;
  }
  &__title {
    font-size: 18px;
    line-height: 24px;
    color: var(--white);
    position: relative;
    text-transform: capitalize;
    margin: 0px 0px 5px;
    display: flex;
    align-content: center;
    align-items: center;
    &-wrapper {
      flex-grow: 1;
      display: flex;
      align-items: flex-start;
      align-content: flex-start;
      flex-direction: column;
    }
    &-group {
      width: 100%;
    }
  }
  &__subtitle {
    margin: 0;
    margin-right: 10px;

    @include tablet {
      max-width: 80%;
      margin-right: 0px;
    }
  }
  &__help-text {
    ::v-deep a {
      color: var(--white);
      text-decoration: underline;
      &:visited {
        color: var(--white);
      }
    }
    ::v-deep u {
      text-decoration: underline;
    }
  }
  &__status-icon {
    margin-left: 10px;
  }
  &__lab-status {
    display: flex;
    align-items: center;
    margin-top: 20px;
  }
  &__btn {
    margin-left: auto;
    margin-top: auto;
    &--disabled {
      cursor: not-allowed;
    }
  }
  &__buttons {
    width: 100%;
    text-align: right;
    &__open-lab {
      margin-left: 15px;
    }
  }
  &__region-selector__wrapper {
    margin-bottom: 15px;
  }
  &__region-info-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: var(--white);
    margin-bottom: 10px;
    span {
      color: var(--carnation);
    }
  }
  &__region-info-icon {
    width: 14.7px;
    cursor: pointer;
    margin-right: 5px;
  }
  &__region-info-error {
    color: var(--carnation);
    margin-left: auto;
  }
  &__region-info-tooltip {
    max-width: 250px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }
  &__action-buttons {
    display: flex;
    align-items: center;
    width: fit-content;
    margin-left: auto;
    &--disabled {
      cursor: not-allowed;
    }
  }
  &__actions {
    margin-left: auto;
    align-self: center;
    &-wrapper {
      display: flex;
      flex-direction: column;
      align-content: flex-start;
      height: 100%;
      justify-content: space-between;
      align-items: flex-end;
      @include tablet {
        max-width: 60%;
      }
    }
    &__switcher {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-bottom: 30px;
      span {
        margin-right: 10px;
        display: inline-block;
        white-space: nowrap;
      }
      &__text {
        color: var(--white);
        text-transform: uppercase;
        line-height: 0;
        transition: 0.5s color ease;
        &--disabled {
          color: var(--light-blue-gray);
        }
      }
    }
  }
  &__time-running {
    color: var(--white);
    width: 100%;
  }
  &__flat-btn {
    background: transparent;
    color: var(--white);
    border: none;
    display: flex;
    align-items: center;
    padding: 0px;
    cursor: pointer;
    &:disabled {
      cursor: not-allowed;
      .pta-card-session__flat-btn-text {
        color: var(--lighter-gray);
        border-color: var(--lighter-gray);
      }
    }
    &-text {
      font-size: 12px;
      font-weight: 700;
      border-bottom: 1px solid var(--white);
      margin: 0px 5px;
    }
  }
  &__check-icon {
    min-width: 16px;
    circle {
      fill: var(--lighter-gray);
    }
    &--success {
      circle {
        fill: var(--lab-status-green);
      }
    }
  }
  &__info-icon {
    color: var(--lighter-gray);
    font-size: 16px;
    margin-right: 5px;
  }
  &__session-details {
    margin-top: 30px;
  }
  &__footer {
    background: linear-gradient(
      112.2deg,
      rgba(187, 188, 199, 0.4) -46.39%,
      rgba(187, 188, 199, 0) 112.38%
    );
    border-radius: 0px 0px 10px 10px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    padding: 5px;
    align-items: center;
    font-size: 12px;
    line-height: 14px;
    &__pill {
      padding: 9px 14px;
      background: linear-gradient(
        111.02deg,
        rgba(18, 18, 18, 0.7) 3.86%,
        rgba(18, 18, 18, 0) 107.96%
      );
      border-radius: 20px;
      margin-right: 10px;
      color: var(--white);
    }
  }
  &__region-selector {
    min-width: 220px;
    max-width: 300px;
    margin-bottom: 10px;
    &__wrapper {
      position: relative;
    }
    &__icon {
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translate(0, -50%);
      pointer-events: none;
    }
  }
  @keyframes rotation {
    from {
      -webkit-transform: rotate(0deg);
    }
    to {
      -webkit-transform: rotate(359deg);
    }
  }
  .modal-message {
    margin: auto;
  }
}
</style>
